import React, { useState } from 'react';
import ScoreBreakdown from './ScoreBreakdown';
import { LeadSuggestion, RejectionReason } from '../services/leadSuggestionService';

interface LeadSuggestionCardProps {
  suggestion: LeadSuggestion;
  onAccept?: (id: string) => void;
  onConvert: (id: string) => void;
  onReject: (id: string, reason?: RejectionReason) => void;
  onArchive?: (id: string) => void;
  onGenerateEmail: (suggestion: LeadSuggestion) => void;
}

const LeadSuggestionCard: React.FC<LeadSuggestionCardProps> = ({
  suggestion,
  onConvert,
  onReject,
  onGenerateEmail,
}) => {
  const [adding, setAdding] = useState(false);
  const location = [suggestion.city, suggestion.state].filter(Boolean).join(', ') || suggestion.location;
  const reason = suggestion.reason_recommended || suggestion.why_this_lead;
  const emailPreview = suggestion.suggested_first_email;
  const isAdded = suggestion.status === 'converted';
  const isCompanyOnly =
    suggestion.contact_status === 'company_only'
    || (!suggestion.decision_maker_name && !suggestion.contact_name && !suggestion.decision_maker_email && !suggestion.email);

  const handleAddToLeads = async () => {
    if (isAdded || adding) return;
    setAdding(true);
    try {
      await onConvert(suggestion.id);
    } finally {
      setAdding(false);
    }
  };

  return (
    <article className="rounded-xl border border-border bg-surface p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-fg">{suggestion.company_name || 'Unknown company'}</h3>
          <p className="text-sm text-fg-muted">
            {(suggestion.decision_maker_name || suggestion.contact_name || 'No contact')}
            {(suggestion.decision_maker_title || suggestion.contact_role)
              ? ` · ${suggestion.decision_maker_title || suggestion.contact_role}`
              : ''}
          </p>
          {isCompanyOnly && (
            <p className="text-xs text-amber-500 mt-1">Company only — decision-maker contact unavailable</p>
          )}
        </div>
        <div className="text-right min-w-[9rem]">
          <ScoreBreakdown
            score={suggestion.lead_score}
            confidence={suggestion.confidence ? Number(suggestion.confidence) : undefined}
            signals={suggestion.opportunity_signals}
          />
          {suggestion.confidence_level ? (
            <p className="text-xs text-fg-muted capitalize mt-1">{suggestion.confidence_level} confidence</p>
          ) : null}
          {suggestion.score_basis === 'base' ? (
            <p className="text-xs text-fg-muted mt-1" title="Scored from source data; deeper research context wasn't available for this lead.">
              Scored without research
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-fg-secondary">
        <p>Location: {location || 'n/a'}</p>
        <p>Industry: {suggestion.industry || 'n/a'}</p>
      </div>

      {reason && (
        <p className="text-sm text-fg-secondary">
          <span className="font-medium text-fg">Why recommended:</span> {reason}
        </p>
      )}

      {suggestion.recommended_service_angle && (
        <p className="text-sm text-fg-secondary">
          <span className="font-medium text-fg">Service angle:</span> {suggestion.recommended_service_angle}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
        <p className="text-fg-muted">Email: {suggestion.email || suggestion.decision_maker_email || 'n/a'}</p>
        <p className="text-fg-muted">Phone: {suggestion.phone || 'n/a'}</p>
        <p className="text-fg-muted">
          Website:{' '}
          {suggestion.website ? (
            <a href={suggestion.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              Visit
            </a>
          ) : (
            'n/a'
          )}
        </p>
      </div>

      {emailPreview && (
        <div className="rounded-lg border border-border bg-app/50 p-3 text-sm text-fg-secondary whitespace-pre-wrap">
          <p className="font-medium text-fg mb-1">Suggested first email</p>
          {emailPreview}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="button"
          onClick={handleAddToLeads}
          className="btn-primary px-4 py-2 text-sm disabled:opacity-60"
          disabled={isAdded || adding}
        >
          {isAdded ? 'In my pipeline' : adding ? 'Adding...' : 'Add to pipeline'}
        </button>
        <button
          type="button"
          onClick={() => onReject(suggestion.id, 'not_relevant')}
          className="px-3 py-1.5 text-sm rounded border border-border text-fg-muted hover:text-fg"
          disabled={suggestion.status === 'rejected'}
        >
          Not interested
        </button>
        <button
          type="button"
          onClick={() => onGenerateEmail(suggestion)}
          className="px-3 py-1.5 text-sm rounded border border-border text-fg hover:bg-surface-muted"
        >
          Draft email
        </button>
      </div>
    </article>
  );
};

export default LeadSuggestionCard;
